import { useState, useEffect, useMemo } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useTagStore } from "@store/tagStore";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { tagsApi, type OrgTag } from "@services/api/tags";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";
import { TAG_COLOR_PALETTE, getContrastTextColor } from "@utils/tagColor";
import {
  NO_CATEGORY_LABEL,
  groupTagsByCategory,
  listTagCategories,
  tagCategory,
} from "@components/ui/tags/TagCategoryGroups";

type TagFormData = { name: string; color: string; scope: string; category: string | null; active: boolean };

/** Valeur du filtre catégorie pour « sans catégorie ». */
const NO_CATEGORY_FILTER = "__none__";

const SCOPE_OPTIONS = [
  { value: "ordre", label: "Ordres" },
  { value: "dossier", label: "Dossiers" },
  { value: "vehicle", label: "Vehicules" },
];

const SCOPE_LABELS: Record<string, string> = {
  ordre: "Ordres",
  dossier: "Dossiers",
  vehicle: "Vehicules",
};

function TagModal({
  tag,
  allTags,
  onClose,
  onSave,
  onDelete,
}: {
  tag: OrgTag | null;
  allTags: OrgTag[];
  onClose: () => void;
  onSave: (data: TagFormData) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(tag?.name || "");
  const [color, setColor] = useState(tag?.color || "#60A5FA");
  const [scope, setScope] = useState(tag?.scope || "ordre");
  const [category, setCategory] = useState(tag ? tagCategory(tag) || "" : "");
  const isCreate = !tag;

  // Catégories déjà utilisées pour ce scope d'abord, puis celles des autres scopes.
  const categorySuggestions = useMemo(() => {
    const own = listTagCategories(allTags, scope);
    const ownLower = new Set(own.map((c) => c.toLowerCase()));
    const others = listTagCategories(allTags).filter((c) => !ownLower.has(c.toLowerCase()));
    return [...own, ...others];
  }, [allTags, scope]);
  const typedCategory = category.trim().toLowerCase();
  const visibleSuggestions = categorySuggestions.filter(
    (c) => !typedCategory || (c.toLowerCase().includes(typedCategory) && c.toLowerCase() !== typedCategory),
  );

  return (
    <BaseModal
      onClose={onClose}
      title={isCreate ? "Nouveau tag" : "Modifier le tag"}
      startIcon={<Icon1 name="tag" size={20} />}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField
          label="Nom du tag"
          value={name}
          onChange={(v: string) => setName(v)}
          placeholder="ex: VIP, Urgent, Huile..."
        />

        <DropDown
          label="Scope"
          value={scope}
          options={SCOPE_OPTIONS}
          onSelect={(v) => setScope(String(v))}
        />

        {/* Catégorie : choisir une existante ou en taper une nouvelle */}
        <div className="flex flex-col gap-1.5">
          <TextField
            label="Catégorie (facultatif)"
            value={category}
            onChange={(v: string) => setCategory(v)}
            placeholder="ex: Pièces, Client, Atelier..."
            items={categorySuggestions.map((c) => ({ label: c }))}
            onSuggestedValue={(v: any) => setCategory(String(v))}
          />
          {visibleSuggestions.length > 0 && (
            <div className="flex flex-row flex-wrap gap-1.5">
              {visibleSuggestions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className="text-h4 rounded-full px-2.5 py-0.5 text-neutral-600 hover:bg-neutral-100"
                  style={{ border: "1px solid #E5E5E5", fontFamily: "Poppins" }}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Couleur</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 6 }}>
            {TAG_COLOR_PALETTE.map((c) => {
              const isSelected = color.toUpperCase() === c.toUpperCase();
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  title={c}
                  className="rounded-full"
                  style={{
                    width: 26,
                    height: 26,
                    background: c,
                    cursor: "pointer",
                    border: "2px solid #FFFFFF",
                    boxShadow: isSelected
                      ? `0 0 0 2px ${c}`
                      : "0 0 0 1px #E5E5E5",
                    transition: "all 0.15s",
                  }}
                />
              );
            })}
          </div>
          {/* Free color picker — any color allowed */}
          <label className="flex flex-row items-center gap-2 mt-1.5 cursor-pointer w-fit">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", padding: 0 }}
            />
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#555" }}>
              Couleur personnalisee ({color.toUpperCase()})
            </span>
          </label>
        </div>

        {/* Preview */}
        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Apercu</span>
          <div className="flex flex-row items-center gap-2">
            <span
              className="text-h3 whitespace-nowrap"
              style={{
                background: color,
                color: getContrastTextColor(color),
                borderRadius: 30,
                padding: "4px 14px",
                lineHeight: "18px",
              }}
            >
              {name || "Tag"}
            </span>
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 pt-2">
          <Button
            label={isCreate ? "Creer" : "Enregistrer"}
            onClick={() =>
              onSave({ name: name.trim(), color, scope, category: category.trim().replace(/\s+/g, " ") || null, active: true })
            }
            disabled={!name.trim()}
            variant="small"
          />
          {!isCreate && onDelete && (
            <SecondaryButton
              label="Supprimer"
              onClick={onDelete}
              variant="small"
              danger
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
}

export const Tags = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const canEdit = isOwnerOrAdmin(userData);

  const storeTags = useTagStore((s) => s.tags);
  const fetchTags = useTagStore((s) => s.fetchTags);
  const addTagToStore = useTagStore((s) => s.addTag);
  const updateTagInStore = useTagStore((s) => s.updateTag);
  const removeTagFromStore = useTagStore((s) => s.removeTag);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterScope, setFilterScope] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [editTarget, setEditTarget] = useState<OrgTag | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!orgData?.id) return;
    fetchTags(orgData.id).then(() => setLoading(false));
  }, [orgData?.id]);

  const filtered = useMemo(() => {
    let list = storeTags;
    if (filterScope !== "all") {
      list = list.filter((t) => t.scope === filterScope);
    }
    if (filterCategory !== "all") {
      list = list.filter((t) => {
        const c = tagCategory(t);
        return filterCategory === NO_CATEGORY_FILTER ? !c : c?.toLowerCase() === filterCategory.toLowerCase();
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(q));
    }
    return list;
  }, [storeTags, search, filterScope, filterCategory]);

  // Catégories proposées dans le filtre : celles du scope affiché.
  const categoryOptions = useMemo(() => {
    const scoped = filterScope === "all" ? storeTags : storeTags.filter((t) => t.scope === filterScope);
    const opts = listTagCategories(scoped).map((c) => ({ value: c, label: c }));
    if (opts.length === 0) return [];
    return [
      { value: "all", label: "Toutes les catégories" },
      ...opts,
      ...(scoped.some((t) => !tagCategory(t)) ? [{ value: NO_CATEGORY_FILTER, label: NO_CATEGORY_LABEL }] : []),
    ];
  }, [storeTags, filterScope]);

  // Le filtre catégorie retombe sur « toutes » s'il ne correspond plus à rien dans le scope.
  useEffect(() => {
    if (filterCategory !== "all" && !categoryOptions.some((o) => o.value === filterCategory)) {
      setFilterCategory("all");
    }
  }, [categoryOptions, filterCategory]);

  // Groupé par scope, puis par catégorie (« Sans catégorie » en dernier).
  const grouped = useMemo(() => {
    const groups: Record<string, OrgTag[]> = {};
    for (const t of filtered) {
      const s = t.scope || "ordre";
      if (!groups[s]) groups[s] = [];
      groups[s].push(t);
    }
    return Object.entries(groups).map(([scope, items]) => ({
      scope,
      count: items.length,
      categories: groupTagsByCategory(items),
    }));
  }, [filtered]);

  const handleCreate = async (data: TagFormData) => {
    if (!orgData?.id) return;
    try {
      const created = await tagsApi.create({ ...data, org_id: orgData.id });
      addTagToStore(created);
      setShowCreate(false);
      showNotification({ message: "Tag cree", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la creation", type: "error" });
    }
  };

  const handleUpdate = async (data: TagFormData) => {
    if (!editTarget) return;
    try {
      const updated = await tagsApi.update(editTarget.id, data);
      updateTagInStore(updated);
      setEditTarget(null);
      showNotification({ message: "Tag mis a jour", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la mise a jour", type: "error" });
    }
  };

  const handleDelete = async () => {
    if (!editTarget) return;
    try {
      await tagsApi.delete(editTarget.id);
      removeTagFromStore(editTarget.id);
      setEditTarget(null);
      showNotification({ message: "Tag supprime", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la suppression", type: "error" });
    }
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Tags</Text1>
          <Text2>Gerez les tags pour vos ordres, dossiers et vehicules.</Text2>
          <Text3>
            Les tags permettent de categoriser et filtrer rapidement vos elements.
            Creez des tags personnalises avec des couleurs pour chaque type d'entite.
          </Text3>
        </div>

        <div className="flex flex-col bg-white rounded-lg border-1 overflow-hidden">
          {/* Header */}
          <div className="flex flex-row items-center justify-between px-4 pt-4 pb-2">
            <span className="text-h3 text-neutral-400">
              Tags ({storeTags.length})
            </span>
          </div>

          {/* Scope filter tabs */}
          <div className="px-4 pb-3">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
              <button
                onClick={() => setFilterScope("all")}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 4, padding: "8px 4px", borderRadius: 10, cursor: "pointer",
                  border: filterScope === "all" ? "2px solid #5C6BFF" : "1.5px solid #E5E5E5",
                  background: filterScope === "all" ? "#F0F1FF" : "#FAFAFA",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: filterScope === "all" ? "#5C6BFF" : "#333", textAlign: "center", lineHeight: "14px" }}>Tous</span>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 10, color: filterScope === "all" ? "#5C6BFF" : "#A1A1A1" }}>{storeTags.length}</span>
              </button>
              {SCOPE_OPTIONS.map((opt) => {
                const isSelected = filterScope === opt.value;
                const count = storeTags.filter((t) => t.scope === opt.value).length;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setFilterScope(opt.value)}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      gap: 4, padding: "8px 4px", borderRadius: 10, cursor: "pointer",
                      border: isSelected ? "2px solid #5C6BFF" : "1.5px solid #E5E5E5",
                      background: isSelected ? "#F0F1FF" : "#FAFAFA",
                      transition: "all 0.15s",
                    }}
                  >
                    <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: isSelected ? "#5C6BFF" : "#333", textAlign: "center", lineHeight: "14px" }}>{opt.label}</span>
                    {count > 0 && <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 10, color: isSelected ? "#5C6BFF" : "#A1A1A1" }}>{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search + Create */}
          <div className="flex flex-row items-center gap-2 px-4 pb-3">
            <div className="flex-1">
              <TextField
                value={search}
                onChange={(v: string) => setSearch(v)}
                placeholder="Chercher .."
                noborder
              />
            </div>
            {categoryOptions.length > 0 && (
              <div style={{ minWidth: 180 }}>
                <DropDown
                  value={filterCategory}
                  options={categoryOptions}
                  onSelect={(v) => setFilterCategory(String(v))}
                />
              </div>
            )}
            {canEdit && (
              <SecondaryButton
                label="Creer"
                variant="small"
                startIcon={<Icon1 name="plus" size={12} />}
                onClick={() => setShowCreate(true)}
              />
            )}
          </div>

          {/* List grouped by scope */}
          {loading ? (
            <div className="px-4 py-8 text-center text-h3 text-neutral-300">
              Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-h3 text-neutral-300">
              {search ? "Aucun tag trouve" : "Aucun tag configure"}
            </div>
          ) : (
            <div className="flex flex-col">
              {grouped.map(({ scope, count, categories }) => (
                <div key={scope}>
                  {filterScope === "all" && (
                    <div
                      className="flex flex-row items-center gap-2 px-4 py-2 border-t-1"
                      style={{ background: "#FAFAFA" }}
                    >
                      <span className="text-h4 font-semibold text-neutral-500">
                        {SCOPE_LABELS[scope] || scope}
                      </span>
                      <span className="text-h4 text-neutral-400">
                        {count} tag{count > 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                  {categories.map(({ category, tags: items }) => (
                    <div key={category ?? NO_CATEGORY_FILTER}>
                  {categories.some((g) => g.category) && (
                    <div className="flex flex-row items-center gap-2 px-4 pt-2.5 pb-1 border-t-1">
                      <span className="text-h5 font-semibold uppercase tracking-wide text-neutral-400">
                        {category ?? NO_CATEGORY_LABEL}
                      </span>
                      <span className="text-h5 text-neutral-300">{items.length}</span>
                    </div>
                  )}
                  {items.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex flex-row items-center px-4 py-3 border-t-1 hover:bg-neutral-50 transition-colors"
                      style={{ gap: 12 }}
                    >
                      <span
                        className="text-h3 whitespace-nowrap"
                        style={{
                          background: tag.color,
                          color: getContrastTextColor(tag.color),
                          borderRadius: 30,
                          padding: "4px 14px",
                          lineHeight: "18px",
                        }}
                      >
                        {tag.name}
                      </span>
                      <span className="text-h3 flex-1" style={{ color: tag.active ? "#525252" : "#A1A1A1", fontFamily: "Poppins" }}>
                        {SCOPE_LABELS[tag.scope] || tag.scope}
                      </span>
                      {canEdit && (
                        <SecondaryButton
                          label="Gerer"
                          variant="small"
                          onClick={() => setEditTarget(tag)}
                        />
                      )}
                    </div>
                  ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <TagModal
          tag={null}
          allTags={storeTags}
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
        />
      )}
      {editTarget && (
        <TagModal
          tag={editTarget}
          allTags={storeTags}
          onClose={() => setEditTarget(null)}
          onSave={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
